import { format, parseArray } from "utils/helpers"
import MessagesHash from "./messages-type"
import { JoinRequestError, PlayError, ServerError } from "play/play-model"
import { Duration } from "moment"
import { asProblemDetails } from "models/dto"
import { KeyboardAnswer } from "models/tables"

function pluralize(
  str0: string,
  str1: string,
  strN: string,
  n: number
): string {
  switch (n) {
    case 0:
      return str0
    case 1:
      return str1
    default:
      return format(strN, n)
  }
}

export interface Messages extends MessagesHash {
  gradesAll: string[]
  subjectsAll: string[]
  schoolsDefaultClassStudents: string[]

  countLicenses(n: number): string

  viewTimesPlayed(n: number): string

  countScreens(n: number): string

  reportsCountStudents(n: number): string

  reportsCountScreens(n: number): string

  reportsTotalPoints(n: number): string

  reportsSuccessRate(percent: number): string

  reportsCorrectAnswers(count: number, percent: number): string

  reportsWrongAnswers(count: number, percent: number): string

  reportsUnattemptedAnswers(count: number, percent: number): string

  errorImageSizeTooBig(maxSize: string): string

  errorImageExtensionNotSupported(supportedExtensionsList: string): string

  errorFileSizeTooBig(maxSize: string): string

  errorFileExtensionNotSupported(supportedExtensionsList: string): string

  errorRequired(fieldName: string): string

  errorCantLoadLesson(httpStatusCode: number): string

  pageTitle(title: string): string

  pageTitleViewLesson(lessonName: string): string

  pageTitleEdit(what: string): string

  pageTitleEditClasses(what: string): string

  pageTitleViewClass(what: string): string

  pageTitleEditSchool(what: string): string

  pageTitleViewSchool(what: string): string

  pageTitleEditPlaySession(name: string | null): string

  menuAvatar(userName: string): string

  viewAveragePoints(points: number): string

  mapMaxAnswersReached(max: number): string

  confirmDeleteLesson(what: string): string

  selectClass(what: string): string

  studentsAdd(what: string): string

  studentsErrorDuplicate(what: string): string

  reportsDurationSeconds(seconds: number): string

  homeworkDeadline(time: string): string

  homeworkActiveUntil(time: string): string

  homeworkCompletedBefore(time: string): string

  homeworkLessonActive(name: string): string

  nvoTitleByGameCode(code: string): string

  joinErrorToString(error: JoinRequestError): string

  serverErrorToString(error: ServerError): string | null

  /** What to tell the player about a failed server request (PlayErrorNotice) */
  playErrorToString(error: PlayError): string

  pointsToString(points: number): string

  durationToWords(duration: Duration): string

  feedbackPointsToString(points: number): string

  pageNotFoundToString(url: string): string

  userIsTeacher(name: string): string

  viewOriginalLesson(teacherName: string): string

  classesRemainingStudents(args: { max: number; remaining: number }): string

  classesStudentLimitReached(limit: number): string

  gameRequiresLogin(args: {
    loginUrl: string
    secondsRemaining: number
  }): string

  classesInvitationCode(code: string): string

  classesMaxStudents(count: number): string

  lessonAuthor(name: string): string

  technicalServerError(e: any): string

  noVideosFoundForFilter(filter: string): string

  keyboardAnswerText(answer: KeyboardAnswer): string
}

export function catalogToMessages(m: MessagesHash): Messages {
  const gradesAllArray = parseArray(m.gradesAllList)
  const subjectsAllArray = parseArray(m.subjectsAllList)
  const schoolsDefaultClassStudentsArray = parseArray(
    m.schoolsDefaultClassStudentsList
  )

  return {
    ...m,

    keyboardAnswerText(answer: KeyboardAnswer): string {
      if (answer.isExact) {
        return format(m.keyboardAnswerExact, answer.content)
      } else {
        return format(m.keyboardAnswerContains, answer.content)
      }
    },

    noVideosFoundForFilter(filter: string): string {
      return format(m.noVideosFoundForFilterFormat, filter)
    },

    countLicenses(n: number): string {
      return format(m.countLicensesFmt, n)
    },
    technicalServerError(e: any): string {
      let error = m.serverError_generic
      const pd = asProblemDetails(e)
      if (pd) {
        error += "\n" + pd.detail
      }
      return error
    },

    lessonAuthor(name: string): string {
      return format(m.lessonAuthorFormat, name)
    },

    classesMaxStudents(count: number): string {
      return format(m.classesMaxStudentsFormat, count)
    },

    classesInvitationCode(code: string): string {
      return format(m.classesInvitationCodeFormat, code)
    },

    gameRequiresLogin(args: {
      loginUrl: string
      secondsRemaining: number
    }): string {
      return format(
        m.gameRequiresLoginFormat,
        args.loginUrl,
        args.secondsRemaining
      )
    },
    classesStudentLimitReached(limit: number): string {
      return format(m.classesStudentLimitReachedFormat, limit)
    },
    classesRemainingStudents(args: { max: number; remaining: number }): string {
      return format(m.classesRemainingStudentsFormat, args.max, args.remaining)
    },
    viewTimesPlayed: pluralize.bind(
      m,
      m.viewTimesPlayed0,
      m.viewTimesPlayed1,
      m.viewTimesPlayedN
    ),
    countScreens: pluralize.bind(
      m,
      m.countScreens0,
      m.countScreens1,
      m.countScreensN
    ),
    reportsCountStudents: pluralize.bind(
      m,
      m.reportsCountStudents0,
      m.reportsCountStudents1,
      m.reportsCountStudentsN
    ),
    reportsCountScreens: pluralize.bind(
      m,
      m.reportsCountScreens0,
      m.reportsCountScreens1,
      m.reportsCountScreensN
    ),
    reportsTotalPoints: pluralize.bind(
      m,
      m.reportsTotalPoints0,
      m.reportsTotalPoints1,
      m.reportsTotalPointsN
    ),
    reportsSuccessRate: function (percent: number): string {
      return format(m.reportsSuccessRateFormat, Math.round(percent))
    },
    reportsCorrectAnswers: function (count: number, percent: number) {
      return format(m.reportsCorrectAnswersFormat, count, Math.round(percent))
    },
    reportsWrongAnswers: function (count: number, percent: number) {
      return format(m.reportsWrongAnswersFormat, count, Math.round(percent))
    },
    reportsUnattemptedAnswers: function (count: number, percent: number) {
      return format(
        m.reportsUnattemptedAnswersFormat,
        count,
        Math.round(percent)
      )
    },

    errorImageSizeTooBig: function (maxSize: string) {
      return format(m.errorImageSizeTooBigFormat, maxSize)
    },
    errorImageExtensionNotSupported: function (
      supportedExtensionsList: string
    ) {
      return format(
        m.errorImageExtensionNotSupportedFormat,
        supportedExtensionsList
      )
    },
    errorFileSizeTooBig: function (maxSize: string) {
      return format(m.errorFileSizeTooBigFormat, maxSize)
    },
    errorFileExtensionNotSupported: function (supportedExtensionsList: string) {
      return format(
        m.errorFileExtensionNotSupportedFormat,
        supportedExtensionsList
      )
    },
    errorRequired: function (fieldName: string) {
      return format(m.errorRequiredFormat, fieldName)
    },
    errorCantLoadLesson: function (httpStatusCode: number) {
      return format(m.errorCantLoadLessonFormat, httpStatusCode)
    },
    pageTitle: function (title: string) {
      return format(m.pageTitleFormat, title)
    },
    pageTitleViewLesson(lessonName: string): string {
      return format(m.pageTitleViewLessonFormat, lessonName)
    },
    pageTitleEdit: function (what: string) {
      return format(m.pageTitleEditFormat, what)
    },
    pageTitleEditClasses: function (what: string) {
      return format(m.pageTitleEditClassesFormat, what)
    },
    pageTitleViewClass: function (what: string) {
      return format(m.pageTitleViewClassFormat, what)
    },
    pageTitleEditSchool: function (what: string) {
      return format(m.pageTitleEditSchoolFormat, what)
    },
    pageTitleViewSchool: function (what: string) {
      return format(m.pageTitleViewSchoolFormat, what)
    },
    pageTitleEditPlaySession: function (name: string | null) {
      if (name) {
        return format(m.pageTitleEditPlaySessionFormat, name)
      } else {
        return m.pageTitlePlaySessions
      }
    },
    menuAvatar: function (userName: string) {
      return format(m.menuAvatarFormat, userName)
    },
    gradesAll: gradesAllArray,
    subjectsAll: subjectsAllArray,
    viewAveragePoints: function (points: number) {
      return format(m.viewAveragePointsFormat, points)
    },
    mapMaxAnswersReached: function (max: number) {
      return format(m.mapMaxAnswersReachedFormat, max)
    },
    confirmDeleteLesson: function (what: string) {
      return format(m.confirmDeleteLessonFormat, what)
    },
    schoolsDefaultClassStudents: schoolsDefaultClassStudentsArray,
    selectClass: function (what: string) {
      return format(m.selectClassFormat, what)
    },
    studentsAdd: function (what: string) {
      return format(m.studentsAddFormat, what)
    },
    studentsErrorDuplicate: function (what: string) {
      return format(m.studentsErrorDuplicateFormat, what)
    },
    reportsDurationSeconds: function (seconds: number) {
      return format(m.reportsDurationSecondsFormat, seconds)
    },
    homeworkDeadline: function (time: string) {
      return format(m.homeworkDeadlineFormat, time)
    },
    homeworkActiveUntil: function (time: string) {
      return format(m.homeworkActiveUntilFormat, time)
    },
    homeworkCompletedBefore: function (time: string) {
      return format(m.homeworkCompletedBeforeFormat, time)
    },
    homeworkLessonActive: function (name: string) {
      return format(m.homeworkLessonActiveFormat, name)
    },
    nvoTitleByGameCode: function (code: string) {
      switch (code) {
        case "3333":
          return "4. клас Български език и литература"
        case "1111":
          return "4. клас Математика"
        case "7777":
          return "7. клас Български език и литература"
        case "5555":
          return "7. клас Математика"
        default:
          return code
      }
    },

    joinErrorToString: function (error: JoinRequestError) {
      switch (error) {
        case JoinRequestError.none:
          return m.joinRequestError_none
        case JoinRequestError.alreadyPlayed:
          return m.joinRequestError_alreadyPlayed
        case JoinRequestError.requireLogin:
          return m.joinRequestError_requireLogin
        case JoinRequestError.studentNotInClass:
          return m.joinRequestError_studentNotInClass
        case JoinRequestError.usernameTaken:
          return m.joinRequestError_usernameTaken
        case JoinRequestError.gameCodeMissing:
          return m.loginPage_gameCodeMissing
        case JoinRequestError.usernameTooShort:
          return m.register_usernameMinLength
        case JoinRequestError.gameCodeExpired:
          return m.joinRequestError_gameCodeExpired
        case JoinRequestError.gameCodeInactive:
          return m.joinRequestError_gameCodeInactive
        case JoinRequestError.gameStarted:
          return m.joinRequestError_gameStarted
        case JoinRequestError.lessonHasNoScreens:
          return m.joinRequestError_lessonHasNoScreens
        default:
          return m.serverError_generic
      }
    },
    serverErrorToString: function (error: ServerError) {
      switch (error) {
        case null:
          return null
        case "ConnectionLost":
          return m.serverError_connectionLost
        case "Generic":
          return m.serverError_generic
        case "FailedToStartClassroomGame":
          return m.serverError_failedToStartClassroomGame
        case "ReportNotFound":
          return m.serverError_reportNotFound
        case "NotLoggedIn":
          return m.serverError_notLoggedIn
      }
    },
    playErrorToString: function (error: PlayError) {
      if (error.level === "fatal") {
        return m.playError_connectionLost
      }
      if (error.level === "behind") {
        return m.playError_alreadyPassed
      }
      switch (error.action) {
        case "Answer":
          return m.playError_answer
        case "NextScreen":
          return m.playError_nextScreen
        case "ReviewNext":
        case "ReviewPrevious":
          return m.playError_review
        case "ToggleTimer":
          return m.playError_timer
        case "StartGame":
          return m.playError_startGame
        default:
          return m.playError_generic
      }
    },
    pointsToString: function (points: number) {
      return format(m.lessonPage_points, points)
    },
    durationToWords(duration: Duration): string {
      let txt = ""
      const hours = duration.hours()
      const minutes = duration.minutes()
      const seconds = duration.seconds()
      const totalSeconds = duration.asSeconds()

      if (hours > 0) {
        if (hours > 1) {
          txt += format(m.time_hours, Math.round(hours))
        } else {
          txt += m.time_hour
        }
      }

      if (minutes > 0) {
        if (hours > 0 && seconds > 0) {
          txt += ","
        } else if (hours > 0 && seconds == 0) {
          txt += m.time_separator
        }

        if (minutes > 1) {
          txt += format(m.time_minutes, Math.round(minutes))
        } else {
          txt += m.time_minute
        }
      }

      if (seconds > 0) {
        if (hours > 0 || minutes > 0) txt += m.time_separator

        if (seconds > 1) {
          if (totalSeconds < 60) {
            txt += format(m.time_seconds, totalSeconds.toFixed(1))
          } else {
            txt += format(m.time_seconds, seconds)
          }
        } else {
          if (totalSeconds < 2) {
            txt += format(m.time_seconds, totalSeconds.toFixed(1))
          } else {
            txt += m.time_second
          }
        }
      }

      if (totalSeconds < 1) {
        txt = m.time_underOneSecond
      }

      return txt
    },

    feedbackPointsToString: function (points: number) {
      if (points > 0) {
        return format(m.feedback_pointsPositive, points)
      } else {
        return m.feedback_pointsZero
      }
    },
    pageNotFoundToString: function (url: string) {
      return format(m.pageNotFound_content, url)
    },
    userIsTeacher: function (name: string) {
      return format(m.loginPage_userIsTeacher, name)
    },
    viewOriginalLesson(teacherName: string): string {
      return format(m.viewOriginalLessonFormat, teacherName)
    },
  }
}

export default Messages
